import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Phone, Mail, Clock, MessageSquare, User, ChevronRight, Plus } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const STATUSES = ['new', 'contacted', 'converted', 'closed'] as const;
const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  new: { label: 'New', color: 'text-blue-700', bg: 'bg-blue-50 border-blue-200' },
  contacted: { label: 'Contacted', color: 'text-yellow-700', bg: 'bg-yellow-50 border-yellow-200' },
  converted: { label: 'Converted', color: 'text-green-700', bg: 'bg-green-50 border-green-200' },
  closed: { label: 'Closed', color: 'text-gray-700', bg: 'bg-gray-50 border-gray-200' },
};

export function LeadPipeline() {
  const { user, hasRole } = useAuth();
  const queryClient = useQueryClient();
  const [selectedLead, setSelectedLead] = useState<any>(null);
  const [noteText, setNoteText] = useState('');
  const isAgency = hasRole('agency_admin');

  const { data: leads, isLoading } = useQuery({
    queryKey: ['lead-pipeline', user?.id],
    queryFn: async () => {
      const { data: userProperties } = await supabase
        .from('properties').select('id').eq('user_id', user!.id);
      const propIds = (userProperties || []).map(p => p.id);

      let qb = supabase
        .from('leads')
        .select('*, properties(title, city), projects(name, city), lead_notes(id, note, created_at)')
        .order('created_at', { ascending: false })
        .limit(200);

      if (propIds.length > 0) {
        qb = qb.or(`agent_id.eq.${user!.id},property_id.in.(${propIds.join(',')})`);
      } else {
        qb = qb.eq('agent_id', user!.id);
      }

      const { data, error } = await qb;
      if (error) throw error;
      return (data || []) as any[];
    },
    enabled: !!user,
  });

  const updateStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { error } = await supabase.from('leads').update({ status: status as any }).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead-pipeline'] });
      toast.success('Lead status updated');
    },
    onError: () => toast.error('Failed to update status'),
  });

  const addNote = useMutation({
    mutationFn: async ({ leadId, note }: { leadId: string; note: string }) => {
      const { error } = await supabase.from('lead_notes').insert({
        lead_id: leadId, user_id: user!.id, note,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead-pipeline'] });
      setNoteText('');
      toast.success('Note added');
    },
    onError: () => toast.error('Failed to add note'),
  });

  // Agents in agency for assignment
  const { data: agencyAgents } = useQuery({
    queryKey: ['agency-agents-for-assign', user?.id],
    queryFn: async () => {
      const { data: agency } = await supabase.from('agencies').select('id').eq('created_by', user!.id).single();
      if (!agency) return [];
      const { data: agents } = await supabase
        .from('agent_profiles')
        .select('user_id, profiles!inner(full_name)')
        .eq('agency_id', agency.id) as any;
      return agents || [];
    },
    enabled: !!user && isAgency,
  });

  const assignLead = useMutation({
    mutationFn: async ({ leadId, agentId }: { leadId: string; agentId: string }) => {
      const { error } = await supabase.from('leads').update({ assigned_to: agentId }).eq('id', leadId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lead-pipeline'] });
      toast.success('Lead assigned');
    },
    onError: () => toast.error('Failed to assign lead'),
  });

  const grouped = STATUSES.reduce((acc, s) => {
    acc[s] = (leads || []).filter((l: any) => l.status === s);
    return acc;
  }, {} as Record<string, any[]>);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <h2 className="font-display text-xl font-bold">Lead Pipeline</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-64 rounded-2xl" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="font-display text-xl font-bold">Lead Pipeline</h2>
        <p className="text-sm text-muted-foreground">Track and manage leads through their lifecycle.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {STATUSES.map(status => {
          const config = STATUS_CONFIG[status];
          const items = grouped[status] || [];
          return (
            <div key={status} className={`rounded-2xl border p-3 ${config.bg} min-h-[200px]`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className={`text-sm font-bold ${config.color}`}>{config.label}</h3>
                <Badge variant="secondary" className="text-xs">{items.length}</Badge>
              </div>
              <div className="space-y-2">
                {items.map((lead: any) => (
                  <Card
                    key={lead.id}
                    className="rounded-xl border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => setSelectedLead(lead)}
                  >
                    <CardContent className="p-3">
                      <div className="flex items-start justify-between">
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold truncate">{lead.name}</div>
                          <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                            <Phone className="h-3 w-3" /> {lead.phone}
                          </div>
                        </div>
                        <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                      </div>
                      {lead.properties && (
                        <div className="text-[10px] text-muted-foreground mt-1.5 truncate">
                          📍 {typeof lead.properties.title === 'object'
                            ? (lead.properties.title as any)?.en
                            : lead.properties.title}, {lead.properties.city}
                        </div>
                      )}
                      {lead.projects && (
                        <div className="text-[10px] text-muted-foreground mt-1.5 truncate">
                          🏗️ {typeof lead.projects.name === 'object'
                            ? (lead.projects.name as any)?.en
                            : lead.projects.name}
                        </div>
                      )}
                      <div className="flex items-center gap-1 mt-2 text-[10px] text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        {formatDistanceToNow(new Date(lead.created_at), { addSuffix: true })}
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {items.length === 0 && (
                  <p className="text-xs text-muted-foreground text-center py-6">No leads</p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Lead Detail Dialog */}
      <Dialog open={!!selectedLead} onOpenChange={(open) => !open && setSelectedLead(null)}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="h-5 w-5" /> {selectedLead?.name}
            </DialogTitle>
          </DialogHeader>
          {selectedLead && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-muted-foreground text-xs">Phone</span>
                  <div className="font-medium">
                    <a href={`tel:${selectedLead.phone}`} className="text-primary hover:underline">{selectedLead.phone}</a>
                  </div>
                </div>
                {selectedLead.email && (
                  <div>
                    <span className="text-muted-foreground text-xs">Email</span>
                    <div className="font-medium">{selectedLead.email}</div>
                  </div>
                )}
                <div>
                  <span className="text-muted-foreground text-xs">Source</span>
                  <div className="font-medium capitalize">{selectedLead.source}</div>
                </div>
                <div>
                  <span className="text-muted-foreground text-xs">Created</span>
                  <div className="font-medium">{new Date(selectedLead.created_at).toLocaleDateString()}</div>
                </div>
              </div>

              {selectedLead.message && (
                <div>
                  <span className="text-muted-foreground text-xs block mb-1">Message</span>
                  <p className="text-sm bg-muted/50 rounded-lg p-3">{selectedLead.message}</p>
                </div>
              )}

              {/* Status change */}
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium">Status:</span>
                <Select
                  value={selectedLead.status}
                  onValueChange={(val) => {
                    updateStatus.mutate({ id: selectedLead.id, status: val });
                    setSelectedLead({ ...selectedLead, status: val });
                  }}
                >
                  <SelectTrigger className="w-[160px] h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUSES.map(s => (
                      <SelectItem key={s} value={s} className="capitalize">{STATUS_CONFIG[s].label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Agency: assign to agent */}
              {isAgency && agencyAgents && agencyAgents.length > 0 && (
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium">Assign to:</span>
                  <Select
                    value={selectedLead.assigned_to || ''}
                    onValueChange={(val) => {
                      assignLead.mutate({ leadId: selectedLead.id, agentId: val });
                      setSelectedLead({ ...selectedLead, assigned_to: val });
                    }}
                  >
                    <SelectTrigger className="w-[200px] h-8 text-sm">
                      <SelectValue placeholder="Select agent" />
                    </SelectTrigger>
                    <SelectContent>
                      {agencyAgents.map((a: any) => (
                        <SelectItem key={a.user_id} value={a.user_id}>
                          {a.profiles?.full_name || 'Agent'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Notes */}
              <div>
                <h4 className="text-sm font-semibold mb-2 flex items-center gap-1">
                  <MessageSquare className="h-4 w-4" /> Notes ({selectedLead.lead_notes?.length || 0})
                </h4>
                {selectedLead.lead_notes && selectedLead.lead_notes.length > 0 && (
                  <div className="space-y-2 mb-3 max-h-40 overflow-y-auto">
                    {selectedLead.lead_notes
                      .sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                      .map((n: any) => (
                        <div key={n.id} className="bg-muted/50 rounded-lg p-2">
                          <p className="text-sm">{n.note}</p>
                          <span className="text-[10px] text-muted-foreground">
                            {formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}
                          </span>
                        </div>
                      ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <Textarea
                    placeholder="Add a note..."
                    value={noteText}
                    onChange={e => setNoteText(e.target.value)}
                    rows={2}
                    className="text-sm"
                  />
                  <Button
                    size="sm"
                    disabled={!noteText.trim() || addNote.isPending}
                    onClick={() => addNote.mutate({ leadId: selectedLead.id, note: noteText.trim() })}
                    className="shrink-0"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Quick actions */}
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="flex-1" asChild>
                  <a href={`tel:${selectedLead.phone}`}><Phone className="h-4 w-4 mr-1" /> Call</a>
                </Button>
                <Button variant="outline" size="sm" className="flex-1" asChild>
                  <a href={`https://wa.me/${selectedLead.phone.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer">
                    <MessageSquare className="h-4 w-4 mr-1" /> WhatsApp
                  </a>
                </Button>
                {selectedLead.email && (
                  <Button variant="outline" size="sm" className="flex-1" asChild>
                    <a href={`mailto:${selectedLead.email}`}><Mail className="h-4 w-4 mr-1" /> Email</a>
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
